﻿import Segment from "../Segment";
import {CustomerAddress, ICustomerAddress } from "./CustomerAddress";
import { Guid } from "../../core/Guid";

export enum BillingType {
    None = 0,
    LicenseActivation = 1,
    ActivationCode = 2
}

export class Customer implements ICustomer {
    constructor(customer?: ICustomer) {
        // initialize typed object from generic object
        this.parentCustomerNumber = customer ? customer.parentCustomerNumber : undefined;
        this.billingCustomerNumber = (customer ? customer.billingCustomerNumber : undefined) ?? "";
        this.customerNumber = (customer ? customer.customerNumber : undefined) ?? "";
        this.name = (customer ? customer.name : undefined) ?? "";
        this.territoryCode = (customer ? customer.territoryCode : undefined) ?? "";
        this.segment = (customer ? customer.segment : undefined) ?? "";
        this.isStudent = (customer ? customer.isStudent : undefined) ?? false;
        this.salespersonCode = (customer ? customer.salespersonCode : undefined) ?? "";
        this.entityId = customer ? customer.entityId : undefined;
        this.credentialId = (customer ? customer.credentialId : undefined) ?? "";
        this.credentialClaims = (customer ? customer.credentialClaims : undefined) ?? "";
        this.paymentTerms = customer?.paymentTerms ? customer.paymentTerms : "";
        this.uniquePriceGroupId = customer?.uniquePriceGroupId;
        this.priceGroupId = (customer ? customer.priceGroupId : undefined) ?? 0;
        this.priceId = (customer ? customer.priceId : undefined) ?? 0;
        this.billingType = (customer ? customer.billingType : undefined) ?? BillingType.None;
        this.creditLimit = customer ? customer.creditLimit : undefined;
        this.creditOnHold = (customer ? customer.creditOnHold : undefined) ?? false;
        this.hideInCart = (customer ? customer.hideInCart : undefined) ?? false;
        this.useAutomatedFulfillment = (customer ? customer.useAutomatedFulfillment : undefined) ?? false;
        this.defaultTaxSchedule = (customer ? customer.defaultTaxSchedule : undefined) ?? "";
        this.comments = (customer ? customer.comments : undefined) ?? "";
        this.address = new CustomerAddress(customer ? customer.address : undefined, this);
    }

    toJSON(): object {
        // return generic object with properties to stringify
        return {
            parentCustomerNumber: this.parentCustomerNumber || undefined,
            billingCustomerNumber: this.billingCustomerNumber || "",
            customerNumber: this.customerNumber || "",
            name: this.name || undefined,
            territoryCode: this.territoryCode || undefined,
            segment: this.segment || undefined,
            isStudent: this.isStudent || undefined,
            salespersonCode: this.salespersonCode || undefined,
            entityId: this.entityId || undefined,
            credentialId: this.credentialId || undefined,
            credentialClaims: this.credentialClaims || undefined,
            paymentTerms: this.paymentTerms || undefined,
            uniquePriceGroupId: this.uniquePriceGroupId,
            priceGroupId: this.priceGroupId,
            priceId: this.priceId,
            billingType: this.billingType || undefined,
            creditLimit: this.creditLimit || undefined,
            creditOnHold: this.creditOnHold || undefined,
            hideInCart: this.hideInCart || undefined,
            useAutomatedFulfillment: this.useAutomatedFulfillment || undefined,
            defaultTaxSchedule: this.defaultTaxSchedule || undefined,
            comments: this.comments || undefined,
            address: this.address.toJSON()
        };
    };

    parentCustomerNumber?: string;
    billingCustomerNumber: string;
    customerNumber: string;
    name: string;
    territoryCode: string;
    segment: string;
    isStudent: boolean;
    salespersonCode: string;
    entityId?: Guid;
    credentialId: string;
    credentialClaims: string;
    paymentTerms: string;
    uniquePriceGroupId?: number;
    priceGroupId: number;
    priceId: number;
    billingType: BillingType;
    creditLimit?: number;
    creditOnHold: boolean;
    hideInCart: boolean;
    useAutomatedFulfillment: boolean;
    defaultTaxSchedule: string;
    address: CustomerAddress;
    comments: string;

    // Calculated properties

    get segmentGroup(): string|undefined {
        return this.segment ? new Segment(this.segment).rootSegment.code : undefined;
    }

    get segmentSubGroup(): string|undefined {
        const segment = this.segment ? new Segment(this.segment).parentSegment : undefined;
        return segment && !segment.isRoot ? segment.code : undefined;
    }

    get inheritsPricing(): boolean {
        return !this.uniquePriceGroupId || this.uniquePriceGroupId !== this.priceGroupId;
    }

    get showInCart(): boolean {
        return !this.hideInCart;
    }

    toString(): string {
        return `${this.parentCustomerNumber ? `(${this.parentCustomerNumber}) ` : ""}${this.customerNumber}: ${this.segment}${this.isStudent ? "*" : ""}; ${this.name}\r\n${this.address}`;
    }
}

export interface ICustomer {
    parentCustomerNumber?: string;
    billingCustomerNumber?: string;
    customerNumber?: string;
    name?: string;
    territoryCode?: string;
    segment?: string;
    isStudent?: boolean;
    salespersonCode?: string;
    entityId?: Guid;
    credentialId?: string;
    credentialClaims?: string;
    paymentTerms?: string;
    uniquePriceGroupId?: number;
    priceGroupId?: number;
    priceId?: number;
    billingType?: BillingType;
    creditLimit?: number;
    creditOnHold?: boolean;
    hideInCart?: boolean;
    useAutomatedFulfillment?: boolean;
    defaultTaxSchedule?: string;
    address: ICustomerAddress;
    comments?: string;
}